import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import type { ExportAccountFormData } from '@jwp/ott-common/types/account';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { object, string } from 'yup';

import { useAriaAnnouncer } from '../../containers/AnnouncementProvider/AnnoucementProvider';
import { modalURLFromLocation } from '../../utils/location';
import Button from '../Button/Button';
import PasswordField from '../PasswordField/PasswordField';
import useQueryParam from '../../hooks/useQueryParam';
import FormFeedback from '../FormFeedback/FormFeedback';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import { useTheme } from '../../../../../ThemeContext';

import styles from './ExportData.module.scss';

const ExportData = () => {
  const accountController = getModule(AccountController);

  const { t } = useTranslation(['user', 'common']);
  const [isSuccess, setIsSuccess] = useState(false);
  const { universalStyles } = useTheme();

  const announce = useAriaAnnouncer();
  const u = useQueryParam('u');

  const navigate = useNavigate();
  const location = useLocation();

  const { handleSubmit, handleChange, submitting, values, errors } = useForm<ExportAccountFormData>({
    initialValues: { password: '' },
    validationSchema: object().shape({
      password: string().required(t('user:login.field_required', { field: t('user:password') })),
    }),
    onSubmit: async (values) => {
      await accountController.exportAccountData(values.password);
    },
    onSubmitSuccess: () => {
      setIsSuccess(true);
      navigate(modalURLFromLocation(location, 'export-data-success'), { replace: true });
    },
    onSubmitError: () => {},
  });

  // step 2: success message
  if (isSuccess) {
    return (
      <div className={styles.successBox}>
        <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
          <ellipse cx="29.0014" cy="28.9881" rx="25.7045" ry="25.6927" fill="#E32652" />
          <ellipse cx="29" cy="28.9866" rx="29" ry="28.9866" fill="#22C55E" />
          <path d="M17 30.186L24.1588 37.2843C24.5486 37.6708 25.1772 37.6708 25.567 37.2843L42 20.9902" stroke="white" strokeWidth="4" strokeLinecap="round" />
        </svg>

        <h2 className={styles.title}>{t(`user:account.export_data_succeeded`)}</h2>
        <p className={styles.paragraph}>{t(`user:account.export_data_succeeded_content`)}</p>
        <Button label={t('alert.close')} color="primary" size="exlarge" onClick={() => navigate('u/my-account')} variant="contained" fullWidth />
      </div>
    );
  }

  // step 1: enter your password and submit
  return (
    <form onSubmit={handleSubmit} noValidate>
      {submitting && <LoadingOverlay transparentBackground inline />}
      <h2 className={styles.heading} style={universalStyles.primaryTextColor}>
        {t('user:account.export_data')}
      </h2>
      <p className={styles.paragraph} style={universalStyles.inActiveColor}>
        {t('user:account.export_data_content')}
      </p>
      {errors.form && <FormFeedback variant="error">{errors.form}</FormFeedback>}
      <PasswordField
        value={values.password}
        onChange={handleChange}
        label={t('user:account.password')}
        placeholder={t('user:account.placeholder')}
        error={!!errors.password || !!errors.form}
        helperText={errors.password}
        name="password"
        showHelperText={false}
        required
      />
      <div>
        <Button
          type="submit"
          color="primary"
          size="exlarge"
          variant="contained"
          label={t('user:account.export_data')}
          fullWidth
          style={universalStyles.signUpButton}
          textStyle={universalStyles.signUpButtonText}
        />
      </div>
    </form>
  );
};

export default ExportData;
